import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// dayjs 한국어 설정
dayjs.locale('ko');

// 다크 모드 스타일 상수
const darkModeStyles = {
  container: {
    p: 2,
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    minHeight: '100vh',
  },
  paper: {
    p: 3,
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
  },
  title: {
    color: '#fff',
    fontSize: '28px',
    fontWeight: 'bold',
    mb: 4,
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  textField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#60a5fa',
    },
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      color: '#fff',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(96, 165, 250, 0.3)',
        },
      },
    },
  },
  datePickerTextField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '& .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  timeInputField: {
    width: '44px',
    '& .MuiInputBase-root': {
      height: '40px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 6px',
      textAlign: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  searchButton: {
    backgroundColor: '#60a5fa',
    color: '#fff',
    borderRadius: '10px',
    padding: '10px 24px',
    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
    fontWeight: 'bold',
    textTransform: 'none',
    minWidth: '120px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#3b82f6',
      boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
      transform: 'translateY(-2px)',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  tableHead: {
    backgroundColor: 'rgba(45, 45, 65, 0.9)',
    '& .MuiTableCell-head': {
      color: '#fff',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transition: 'background-color 0.3s ease',
    },
    '& .MuiTableCell-body': {
      color: '#fff',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
  },
  tableCell: {
    color: '#fff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  tablePagination: {
    marginTop: '12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(45, 45, 65, 0.8)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '& .MuiTablePagination-toolbar': {
      justifyContent: 'center',
    },
    '& .MuiTablePagination-select': {
      padding: '6px 10px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
      fontWeight: 'bold',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '& .MuiSelect-icon': {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
    '& .MuiTablePagination-actions': {
      color: '#fff',
      '& .MuiIconButton-root': {
        color: 'rgba(255, 255, 255, 0.7)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.3)',
        },
      },
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  loadingIndicator: {
    color: '#60a5fa',
    margin: '40px 0',
  },
  noDataMessage: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontStyle: 'italic',
    padding: '40px 0',
  },
};

// 커스텀 날짜/시간 입력 컴포넌트
const CustomDateTimePicker = ({ label, value, onChange }) => {
  const [date, setDate] = useState(value ? dayjs(value) : dayjs());
  const [time, setTime] = useState({
    hour: value ? value.format('HH') : '00',
    minute: value ? value.format('mm') : '00',
    second: value ? value.format('ss') : '00',
  });

  // 날짜 변경 핸들러
  const handleDateChange = newDate => {
    if (!newDate) return;
    setDate(newDate);
    const combinedDateTime = newDate.hour(parseInt(time.hour)).minute(parseInt(time.minute)).second(parseInt(time.second));
    onChange(combinedDateTime);
  };

  // 시간 변경 핸들러
  const handleTimeChange = (field, value) => {
    const newValue = value.replace(/\D/g, ''); // 숫자가 아닌 문자 제거
    let parsedValue = parseInt(newValue) || 0;

    // 각 필드별 유효성 검사
    if (field === 'hour' && parsedValue > 23) parsedValue = 23;
    if ((field === 'minute' || field === 'second') && parsedValue > 59) parsedValue = 59;

    const formattedValue = parsedValue.toString().padStart(2, '0');

    setTime(prev => ({ ...prev, [field]: formattedValue }));

    // 새로운 날짜와 시간 조합
    const combinedDateTime = date
      .hour(field === 'hour' ? parsedValue : parseInt(time.hour))
      .minute(field === 'minute' ? parsedValue : parseInt(time.minute))
      .second(field === 'second' ? parsedValue : parseInt(time.second));

    onChange(combinedDateTime);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <DateTimePicker
        label={label}
        value={date}
        onChange={handleDateChange}
        sx={{ flex: 1 }}
        format="YYYY-MM-DD HH:mm"
        slotProps={{
          textField: {
            sx: darkModeStyles.datePickerTextField,
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: 'rgba(35, 35, 50, 0.95)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '& .MuiPickersDay-root': {
                  color: '#fff',
                  '&.Mui-selected': {
                    backgroundColor: '#60a5fa',
                    color: '#fff',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(96, 165, 250, 0.2)',
                  },
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiPickersCalendarHeader-label': {
                  color: '#fff',
                },
                '& .MuiIconButton-root': {
                  color: '#fff',
                },
              },
            },
          },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* <TextField
          value={time.hour}
          onChange={e => handleTimeChange('hour', e.target.value)}
          size="small"
          variant="outlined"
          sx={darkModeStyles.timeInputField}
          inputProps={{
            maxLength: 2,
          }}
        />
        <Typography sx={{ fontWeight: 500, color: '#fff' }}>:</Typography>
        <TextField
          value={time.minute}
          onChange={e => handleTimeChange('minute', e.target.value)}
          size="small"
          variant="outlined"
          sx={darkModeStyles.timeInputField}
          inputProps={{
            maxLength: 2,
          }}
        />
        <Typography sx={{ fontWeight: 500, color: '#fff' }}>:</Typography>
        <TextField
          value={time.second}
          onChange={e => handleTimeChange('second', e.target.value)}
          size="small"
          variant="outlined"
          sx={darkModeStyles.timeInputField}
          inputProps={{
            maxLength: 2,
          }}
        /> */}
      </Box>
    </Box>
  );
};

const MoneySendHistory = () => {
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery('(min-width:600px)');

  const [filters, setFilters] = useState({
    agentId: '',
    type: '',
    gameCompany: '',
    startDate: null,
    endDate: null,
  });

  const [data, setData] = useState([
    {
      transactionId: '3489051485',
      agent: 'ch1234',
      type: '지급',
      chargeAmount: 100000000,
      balanceBefore: 100300000,
      balanceAfter: 300000,
      income: 5000000,
      subAgent: '하니',
      subAgentId: 'park8888',
      processDate: '2025-01-04 02:26:56',
    },
  ]);

  const totalChargeAmount = data.reduce((acc, row) => acc + row.chargeAmount, 0);
  const totalIncomeAmount = data.reduce((acc, row) => acc + row.income, 0);

  const handleFilterChange = (field, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    console.log('검색 조건:', filters);
    // 검색 로직 추가 가능
  };

  // 페이지네이션 상태
  const [page, setPage] = useState(0); // 현재 페이지 번호
  const [rowsPerPage, setRowsPerPage] = useState(30); // 한 페이지에 보여지는 행 개수

  // 페이지 변경 핸들러
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 보여지는 행 개수 변경 핸들러
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // 페이지를 첫 번째로 초기화
  };

  const [anchorEl, setAnchorEl] = useState(null); // 메뉴 앵커 엘리먼트
  const [selectedAgent, setSelectedAgent] = useState(null); // 선택된 하위 에이전트

  const [selectedRowId, setSelectedRowId] = useState(null); // 선택된 row의 id 저장

  const handleMenuOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleMenuItemClick = action => {
    if (action === 'edit') {
      navigate(`/agent/update/${selectedRowId}`);
    }

    if (action === 'history') {
      navigate(`/money-send-history?agentId=${selectedRowId}`);
    }

    if (action === 'charge') {
      navigate(`/money-charge-history?agentId=${selectedRowId}`);
    }

    if (action === 'user') {
      navigate(`/user-list?agentId=${selectedRowId}`);
    }

    if (action === 'payment') {
      navigate(`/money?agentId=${selectedRowId}`);
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
      <Paper sx={{ p: isNonMobile ? 3 : 2, borderRadius: 2 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          지급해준 내역
        </Typography>

        {/* 필터 영역 */}
        <Box
          display="flex"
          flexDirection={isNonMobile ? 'row' : 'column'}
          flexWrap="wrap" // 반응형으로 내용이 자동 줄바꿈되도록 설정
          gap={isNonMobile ? 2 : 1.5} // 반응형에 따라 간격 조정
          alignItems={isNonMobile ? 'center' : 'stretch'} // 데스크톱: 정렬, 모바일: 너비 100%
          sx={{
            mb: 3,
            '& > *': { flex: isNonMobile ? 1 : 'none', minWidth: isNonMobile ? '200px' : '100%' }, // 반응형 크기 최적화
          }}
        >
          {/* 에이전트 아이디 필터 */}
          <FormControl>
            <InputLabel>에이전트 아이디</InputLabel>
            <Select value={filters.agentId} onChange={e => handleFilterChange('agentId', e.target.value)}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="ch1234">ch1234</MenuItem>
              <MenuItem value="ch5678">ch5678</MenuItem>
            </Select>
          </FormControl>

          {/* 타입 필터 */}
          <FormControl>
            <InputLabel>타입</InputLabel>
            <Select value={filters.type} onChange={e => handleFilterChange('type', e.target.value)}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="충전">지급</MenuItem>
              <MenuItem value="출금">차감</MenuItem>
            </Select>
          </FormControl>

          {/* 날짜 필터 */}
          <Box flex={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box display="flex" flexDirection={isNonMobile ? 'row' : 'column'} gap={isNonMobile ? 2 : 1}>
                <CustomDateTimePicker label="시작 날짜 및 시간" value={filters.startDate} onChange={date => handleFilterChange('startDate', date)} />
                <CustomDateTimePicker label="종료 날짜 및 시간" value={filters.endDate} onChange={date => handleFilterChange('endDate', date)} />
              </Box>
            </LocalizationProvider>
          </Box>
          {/* 검색 버튼 */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            sx={{
              width: isNonMobile ? '100px' : '100%', // 데스크톱: 100px, 모바일: 전체 너비
              minWidth: '80px', // 최소 너비 설정
              height: '56px', // 버튼 높이 고정
            }}
          >
            검색
          </Button>
        </Box>

        {/* 테이블 영역 */}
        <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>트랜잭션 번호</TableCell>
                <TableCell>지급 해준 에이전트</TableCell>
                <TableCell>타입</TableCell>
                <TableCell>지급해준 금액</TableCell>
                <TableCell>유통수익</TableCell>
                <TableCell>지급 전 잔액</TableCell>
                <TableCell>지급 후 잔액</TableCell>
                <TableCell>지급 받은 에이전트</TableCell>
                <TableCell>처리 시각</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(row => (
                <TableRow key={row.id}>
                  <TableCell>{row.transactionId}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body1" textAlign="center">
                        {row.agent}
                      </Typography>
                      <Button
                        onClick={event => handleMenuOpen(event, row.id)}
                        sx={{ color: '#673ab7', fontWeight: 'bold', textTransform: 'none' }}
                        variant="outlined"
                      >
                        @{row.agent}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'red' }}>{row.type}</TableCell>
                  <TableCell>{row.chargeAmount.toLocaleString()} 원</TableCell>
                  <TableCell>{row.income.toLocaleString()} 원 ( 0.5 % )</TableCell>
                  <TableCell>{row.balanceBefore.toLocaleString()} 원</TableCell>
                  <TableCell>{row.balanceAfter.toLocaleString()} 원</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body1" textAlign="center">
                        {row.subAgent}
                      </Typography>
                      <Button
                        onClick={event => handleMenuOpen(event, row.id)}
                        sx={{ color: '#673ab7', fontWeight: 'bold', textTransform: 'none' }}
                        variant="outlined"
                      >
                        @{row.subAgentId}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>{row.processDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell variant="body1" sx={{ fontWeight: 'bold' }} colSpan={3}>
                  총합
                </TableCell>
                <TableCell variant="body1" sx={{ fontWeight: 'bold' }}>
                  {totalChargeAmount.toLocaleString()} 원
                </TableCell>
                <TableCell variant="body1" sx={{ fontWeight: 'bold' }}>
                  {totalIncomeAmount.toLocaleString()} 원
                </TableCell>
                <TableCell variant="body1" colSpan={4}></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30, 50, 100]} // 선택 가능한 행 개수 옵션
            component="div"
            count={data.length} // 전체 데이터 수
            rowsPerPage={rowsPerPage} // 현재 설정된 행 개수
            page={page} // 현재 페이지 번호
            onPageChange={handleChangePage} // 페이지 변경 핸들러
            onRowsPerPageChange={handleChangeRowsPerPage} // 행 개수 변경 핸들러
            labelRowsPerPage="" // 'Rows per page' 텍스트 제거
            sx={{
              marginTop: '12px',
              borderRadius: 2, // 둥근 테두리
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)', // 그림자 효과
              '& .MuiTablePagination-toolbar': {
                justifyContent: 'center', // 중앙 정렬
              },
              '& .MuiTablePagination-select': {
                padding: '6px 10px', // 여백 추가
                border: '1px solid #eee', // 테두리 색상
                borderRadius: '8px', // 둥근 모서리
                backgroundColor: 'white', // 배경색
                color: '#673ab7', // 텍스트 색상
                fontWeight: 'bold', // 텍스트 강조
                '&:hover': {
                  backgroundColor: '#f3e5f5', // 호버 효과
                },
                '& .MuiSelect-icon': {
                  color: '#673ab7', // 드롭다운 아이콘 색상
                },
              },
              '& .MuiTablePagination-actions': {
                color: '#673ab7', // 페이지 버튼 강조색
              },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 200, // 드롭다운 최대 높이
                    borderRadius: 2, // 드롭다운 둥근 모서리
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)', // 드롭다운 그림자
                    '& .MuiMenuItem-root': {
                      padding: '8px 16px', // 드롭다운 아이템 여백
                      '&:hover': {
                        backgroundColor: '#f3e5f5', // 드롭다운 아이템 호버
                      },
                    },
                  },
                },
              },
            }}
          />
        </TableContainer>
      </Paper>

      {/* 메뉴 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            width: '150px', // 메뉴 너비 설정
          },
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('edit')}>수정</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('history')}>지급 내역</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('charge')}>충전 내역</MenuItem>
        <Box
          sx={{
            borderTop: '1px solid #ccc',
            mt: 1,
            pt: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ pl: 2, color: 'purple', fontWeight: 'bold' }}>
            유저
          </Typography>
          <MenuItem onClick={() => handleMenuItemClick('user')}>유저 목록</MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('payment')}>지급 내역</MenuItem>
        </Box>
      </Menu>
    </Box>
  );
};

export default MoneySendHistory;
